/* ═══════════════════════════════════════════════════════════════
   Time Sheets + Forecasting — Two separate page components
   TimeSheetsPage : Ajera-style weekly timesheet, submit/approve workflow
   ForecastingPage: 4-week project hour forecasting grid
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Trash2, X, ChevronUp, ChevronDown, Plus } from 'lucide-react';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { SearchableSelect } from '../components/SearchableSelect';
import { Button } from '../components/ui/button';
import EmptyState from '../components/EmptyState';
import { TimeService } from '../services/timeService';
import { TeamService } from '../services/teamService';
import { ProjectService } from '../services/projectService';
import { AdminService } from '../services/adminService';
import { ExpenseService } from '../services/expenseService';
import { VacationService } from '../services/vacationService';
import type { AuthUser } from '../services/authService';
import { hasPermission } from '../../shared/permissions';
// New full-page forecasting grid (week-based, Float-style). Replaces the
// old embedded 4-week forecasting grid as of v2.7.3.
import NewForecastingPage from './ForecastingPage';
import type {
  HourEntry,
  HourForecast,
  TeamMember,
  Project,
  ActivityCode,
  Timesheet,
  OverheadCategory,
  ProjectPhase,
  Expense,
  ExpenseAttachment,
  ExpenseType,
  VacationCategory,
  FirmHoliday,
  VacationRequest,
} from '../../shared/types';

// ─── Date Helpers ───────────────────────────────────────────────

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateShort(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateISO(d: Date): string {
  return d.toISOString().split('T')[0];
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Unwrap a Promise.allSettled result, falling back on rejection so one failed
// fetch never blanks the whole page (DESIGN.md §5 — never Promise.all on
// page data loads).
function settled<T>(r: PromiseSettledResult<T>, fallback: T): T {
  return r.status === 'fulfilled' ? r.value : fallback;
}

// ─── Shared Style Constants ─────────────────────────────────────

const S = {
  hourInput: {
    padding: '5px 4px',
    borderRadius: 4,
    border: '1px solid var(--bd)',
    background: 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 13,
    width: '100%',
    textAlign: 'center' as const,
    fontFamily: "'JetBrains Mono', monospace",
    boxSizing: 'border-box' as const,
  },
  btnNav: {
    padding: '5px 10px',
    borderRadius: 4,
    border: '1px solid var(--bd)',
    background: 'var(--sf)',
    color: 'var(--tx)',
    cursor: 'pointer',
    fontSize: 13,
  } as React.CSSProperties,
  btnPrimary: (disabled?: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 5,
    border: 'none',
    background: disabled ? 'var(--bd)' : 'var(--ac)',
    color: disabled ? 'var(--tm)' : 'var(--color-text-inverse)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    opacity: disabled ? 0.6 : 1,
  }),
  btnDanger: (disabled?: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 5,
    border: 'none',
    background: disabled ? 'var(--bd)' : 'var(--dg)',
    color: disabled ? 'var(--tm)' : 'var(--color-text-inverse)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    opacity: disabled ? 0.6 : 1,
  }),
  btnGhost: {
    padding: '6px 14px',
    borderRadius: 5,
    border: '1px solid var(--bd)',
    background: 'transparent',
    color: 'var(--tx)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  } as React.CSSProperties,
  thCell: {
    padding: '9px 6px',
    fontSize: 11,
    fontWeight: 700,
    textAlign: 'center' as const,
    borderBottom: '1px solid var(--bd)',
    color: 'var(--tm)',
    minWidth: 62,
  } as React.CSSProperties,
  sectionLabel: {
    padding: '8px 12px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '.06em',
    color: 'var(--tm)',
    background: 'var(--sa)',
    borderBottom: '1px solid var(--bd)',
  } as React.CSSProperties,
};

// ─── Status Badge ────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  draft: {
    bg: 'color-mix(in srgb, var(--tm) 14%, transparent)',
    color: 'var(--tm)',
    label: 'Draft',
  },
  submitted: {
    bg: 'color-mix(in srgb, var(--color-info) 15%, transparent)',
    color: 'var(--color-info)',
    label: 'Submitted',
  },
  approved: { bg: 'rgba(var(--okr),0.15)', color: 'var(--ok)', label: 'Approved' },
  locked: {
    bg: 'color-mix(in srgb, var(--color-danger) 14%, transparent)',
    color: 'var(--dg)',
    label: 'Locked',
  },
  rejected: {
    bg: 'color-mix(in srgb, var(--color-danger) 14%, transparent)',
    color: 'var(--dg)',
    label: 'Rejected',
  },
};

function StatusBadge({ status }: { status: Timesheet['status'] }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.04em',
        background: c.bg,
        color: c.color,
        textTransform: 'uppercase',
      }}
    >
      {c.label}
    </span>
  );
}

// ─── Searchable Project Dropdown ─────────────────────────────────

interface ProjectPickerProps {
  value: string;
  projects: Project[];
  onChange: (id: string) => void;
  disabled?: boolean;
}

function ProjectPicker({ value, projects, onChange, disabled }: ProjectPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selected = projects.find((p) => p.id === value);
  const label = selected
    ? `${selected.projectNumber ? selected.projectNumber + ' — ' : ''}${selected.name}`
    : 'Select project...';

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.projectNumber || '').toLowerCase().includes(q) ||
        (p.clientName || '').toLowerCase().includes(q)
    );
  }, [query, projects]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => {
          if (!disabled) {
            setOpen((o) => !o);
            setQuery('');
          }
        }}
        style={{
          padding: '5px 8px',
          borderRadius: 4,
          border: '1px solid var(--bd)',
          background: disabled ? 'var(--sa)' : 'var(--ib)',
          color: selected ? 'var(--tx)' : 'var(--tm)',
          fontSize: 13,
          cursor: disabled ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          userSelect: 'none',
        }}
      >
        {label}
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 999,
            minWidth: 260,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 6,
            boxShadow: 'var(--shadow-lg)',
            marginTop: 2,
          }}
        >
          <div
            style={{
              padding: '6px 8px',
              borderBottom: '1px solid var(--bd)',
              position: 'relative',
            }}
          >
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter..."
              style={{
                width: '100%',
                padding: '5px 8px',
                paddingRight: query ? 22 : 8,
                borderRadius: 4,
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                title="Clear search"
                aria-label="Clear search"
                style={{
                  position: 'absolute',
                  right: 6,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--tm)',
                  cursor: 'pointer',
                  lineHeight: 1,
                  padding: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <X size={12} />
              </button>
            )}
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--tm)' }}>
                No matches
              </div>
            )}
            {filtered.map((p) => (
              <div
                key={p.id}
                onMouseDown={() => {
                  onChange(p.id);
                  setOpen(false);
                  setQuery('');
                }}
                style={{
                  padding: '7px 12px',
                  fontSize: 13,
                  cursor: 'pointer',
                  background: p.id === value ? 'rgba(var(--acr),0.08)' : 'transparent',
                  color: 'var(--tx)',
                  borderBottom: '1px solid var(--bd)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(var(--acr),0.06)')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    p.id === value ? 'rgba(var(--acr),0.08)' : 'transparent')
                }
              >
                {p.projectNumber && (
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      color: 'var(--ac)',
                      marginRight: 6,
                      fontSize: 11,
                    }}
                  >
                    {p.projectNumber}
                  </span>
                )}
                {p.name}
                {p.clientName && (
                  <span style={{ color: 'var(--tm)', fontSize: 11, marginLeft: 6 }}>
                    {p.clientName}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Types ───────────────────────────────────────────────────────

interface PageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: any;
}

// Row in the project-hours section of the timesheet
interface ProjectRow {
  key: string; // stable row key (uuid-like)
  projectId: string;
  phaseDesc: string;
  activityCodeId: string;
}

// Overhead categories are now loaded from Admin Center (configurable)

// ═══════════════════════════════════════════════════════════════
// TIME SHEETS PAGE
// ═══════════════════════════════════════════════════════════════

interface TimeSheetsPageProps extends PageProps {
  initialFilter?: { projectId?: string; weekStart?: string; memberId?: string } | null;
  onInitialFilterConsumed?: () => void;
}

function TimeSheetsPage({
  toast,
  authUser,
  initialFilter,
  onInitialFilterConsumed,
}: TimeSheetsPageProps) {
  const [weekStart, setWeekStart] = useState<Date>(getMonday(new Date()));
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);
  const [overheadCategories, setOverheadCategories] = useState<OverheadCategory[]>([]);
  const [allPhases, setAllPhases] = useState<ProjectPhase[]>([]);
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [allTimesheets, setAllTimesheets] = useState<Timesheet[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [projectRows, setProjectRows] = useState<ProjectRow[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vacationRequests, setVacationRequests] = useState<VacationRequest[]>([]);
  // Forecast rows for the active week — used to pre-fill empty cells with
  // a faded "suggestion" so the user can adopt the forecast in one click.
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [firmHolidays, setFirmHolidays] = useState<FirmHoliday[]>([]);
  const [vacationCategories, setVacationCategories] = useState<VacationCategory[]>([]);
  const rowKeyCounter = useRef(0);

  function makeKey() {
    return `row-${++rowKeyCounter.current}`;
  }

  // ── Load base data (once) ───────────────────────────────────
  useEffect(() => {
    loadBase();
  }, []);
  useEffect(() => {
    const handler = () => loadBase();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  // ── Reload week when member or week changes ─────────────────
  useEffect(() => {
    if (selectedMemberId) loadWeek();
  }, [weekStart, selectedMemberId]);

  // ── One-shot deep-link consumer ──────────────────────────────
  // The dashboard's "burn rate" / "hours" widgets fire a drill that
  // lands here pre-set to a specific week (and optionally a specific
  // member, if a manager clicked through from a team-utilization row).
  useEffect(() => {
    if (!initialFilter) return;
    if (initialFilter.weekStart && /^\d{4}-\d{2}-\d{2}$/.test(initialFilter.weekStart)) {
      const [y, m, d] = initialFilter.weekStart.split('-').map(Number);
      setWeekStart(getMonday(new Date(y, m - 1, d)));
    }
    if (initialFilter.memberId) {
      // Defer until base data loaded — selectedMemberId is set in loadBase.
      setSelectedMemberId(initialFilter.memberId);
    }
    onInitialFilterConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  async function loadBase() {
    try {
      const [projsR, memsR, codesR, ohCatsR, vCatsR, fHolsR] = await Promise.allSettled([
        ProjectService.list(),
        TeamService.list(),
        AdminService.activityCodes.list(),
        AdminService.overheadCategories.list(),
        VacationService.categories.list(),
        VacationService.holidays.list(),
      ]);
      const projs = settled(projsR, [] as Project[]);
      const mems = settled(memsR, [] as TeamMember[]);
      const codes = settled(codesR, [] as ActivityCode[]);
      const ohCats = settled(ohCatsR, [] as OverheadCategory[]);
      const vCats = settled(vCatsR, [] as VacationCategory[]);
      const fHols = settled(fHolsR, [] as FirmHoliday[]);
      const activeProjs = projs.filter((p) => p.status === 'active');
      const activeMembers = mems.filter((m) => m.active);
      setProjects(activeProjs);
      setMembers(activeMembers);
      setActivityCodes(codes);
      setOverheadCategories(ohCats);
      setVacationCategories(vCats);
      setFirmHolidays(fHols);

      // Load phases for all active projects (for phase auto-fill)
      const phases: ProjectPhase[] = [];
      for (const proj of activeProjs) {
        try {
          phases.push(...(await TimeService.listPhases(proj.id)));
        } catch {}
      }
      setAllPhases(phases);

      // Find logged-in user's team member record
      const myId = authUser?.teamMemberId || authUser?.id;
      const me = myId ? activeMembers.find((m) => m.id === myId) : null;
      const selectedMember = me || activeMembers[0];

      // Admin = has permission to view anyone's timesheets (not just their own).
      const isTimeAdmin =
        hasPermission(authUser?.permissions, 'timesheets.view_all') ||
        hasPermission(authUser?.permissions, 'timesheets.approve');
      if (selectedMember) {
        setIsAdmin(isTimeAdmin);
        setSelectedMemberId(selectedMember.id);
      } else {
        setIsAdmin(isTimeAdmin);
      }
    } catch (err: any) {
      setError('Failed to load data: ' + (err.message || 'Unknown error'));
    }
  }

  async function loadWeek() {
    if (!selectedMemberId) return;
    try {
      const weekStartStr = formatDateISO(weekStart);
      const weekEndStr = formatDateISO(addDays(weekStart, 6));

      // Pull timesheet + entries + vacation + forecasts in parallel.
      const [tsRawR, entriesRawR, historyR, vReqsR, fcsR] = await Promise.allSettled([
        TimeService.getTimesheetByWeek(selectedMemberId, weekStartStr),
        TimeService.listEntries(undefined, weekStartStr, weekEndStr, selectedMemberId),
        TimeService.listTimesheets(selectedMemberId),
        VacationService.listRequestsInRange(weekStartStr, weekEndStr, selectedMemberId),
        TimeService.listForecastsForRange(weekStartStr, weekEndStr, [selectedMemberId]).catch(
          () => []
        ),
      ]);
      const tsRaw = settled(tsRawR, null as Timesheet | null);
      const entriesRaw = settled(entriesRawR, [] as HourEntry[]);
      const history = settled(historyR, [] as Timesheet[]);
      const vReqs = settled(vReqsR, [] as VacationRequest[]);
      const fcs = settled(fcsR, [] as HourForecast[]);

      // ─── Auto-seed from forecasts ────────────────────────────────
      // First time a user opens a week, materialize hour_entries from any
      // matching forecasts so the timesheet is pre-filled. This is the
      // whole point of forecasting — plan once, confirm at week-end, save
      // the typing in between.
      //
      // Guard: only seed when the timesheet doesn't exist yet (= never
      // touched). Subsequent reloads NEVER re-seed, so a user clearing
      // a cell to 0 won't see it come back. After this block, `ts` and
      // `allEntries` reflect post-seed state.
      let ts = tsRaw;
      let allEntries = entriesRaw;
      const seedable =
        !ts &&
        fcs.length > 0 &&
        fcs.some((f) => f.sourceKind !== 'vacation' && f.projectId && (Number(f.hours) || 0) > 0);
      if (seedable) {
        try {
          // Create the timesheet wrapper so saveEntry has an id to attach
          // hours to (timesheet locking flows through this id).
          ts = await TimeService.getOrCreateTimesheet(selectedMemberId, weekStartStr);
          // Seed one entry per forecast row. Concurrency cap of 6 keeps
          // the EC2 worker pool unblocked even on a heavy 30-cell week.
          const queue = fcs.filter(
            (f) =>
              f.sourceKind !== 'vacation' && f.projectId && f.dayDate && (Number(f.hours) || 0) > 0
          );
          async function worker() {
            while (queue.length) {
              const f = queue.shift();
              if (!f) return;
              try {
                await TimeService.saveEntry({
                  id: '',
                  projectId: f.projectId!,
                  teamMemberId: selectedMemberId,
                  date: f.dayDate!,
                  hours: Number(f.hours) || 0,
                } as HourEntry);
              } catch (err) {
                // Don't fail the whole load if one seed fails.
                console.warn('[timesheet seed]', f.id, err);
              }
            }
          }
          await Promise.all(Array.from({ length: 6 }, () => worker()));
          // Refetch so the UI sees real ids on the seeded rows.
          allEntries = await TimeService.listEntries(
            undefined,
            weekStartStr,
            weekEndStr,
            selectedMemberId
          );
        } catch (seedErr) {
          console.error('[timesheet seed] failed', seedErr);
          // Fall through with un-seeded data; the placeholder UX still
          // shows the user what was forecasted.
        }
      }

      setTimesheet(ts);
      setEntries(allEntries);
      setAllTimesheets(history.sort((a, b) => b.weekStart.localeCompare(a.weekStart)));
      setVacationRequests(vReqs);

      // Rebuild project rows.  Three sources, in priority order:
      //   1. Existing hour_entries — anything the user has already typed
      //   2. Forecast rows (sourceKind != 'vacation') — pre-fills upcoming
      //      weeks before the user opens them
      //   3. Empty default row when both above are empty
      const seenKeys = new Map<string, ProjectRow>();

      const projectEntries = allEntries.filter((e) => e.projectId && !e.overheadCategory);
      for (const e of projectEntries) {
        const rowKey = `${e.projectId}|${e.activityCodeId || ''}`;
        if (!seenKeys.has(rowKey)) {
          seenKeys.set(rowKey, {
            key: makeKey(),
            projectId: e.projectId,
            phaseDesc: '',
            activityCodeId: e.activityCodeId || '',
          });
        }
      }

      // Inject forecast rows for any project not already represented by an
      // entry. We only seed (project, '') rows — the user picks an activity
      // code on save. Vacation forecasts are skipped here; they render in
      // the locked vacation section.
      for (const f of fcs) {
        if (f.sourceKind === 'vacation') continue;
        if (!f.projectId) continue;
        const rowKey = `${f.projectId}|`;
        if (!seenKeys.has(rowKey) && !projectEntries.some((e) => e.projectId === f.projectId)) {
          seenKeys.set(rowKey, {
            key: makeKey(),
            projectId: f.projectId,
            phaseDesc: '',
            activityCodeId: '',
          });
        }
      }

      if (seenKeys.size > 0) {
        setProjectRows(Array.from(seenKeys.values()));
      } else {
        setProjectRows([
          { key: makeKey(), projectId: projects[0]?.id || '', phaseDesc: '', activityCodeId: '' },
        ]);
      }

      // Cache forecasts so the UI can render "ghost" pre-fills inside empty
      // cells (showing the user what the forecast suggests).
      setForecasts(fcs);
    } catch (err: any) {
      console.error('loadWeek error:', err);
      toast('Error loading timesheet: ' + err.message, true);
    }
  }

  // Ensure a timesheet exists before saving hours — creates on demand
  async function ensureTimesheet(): Promise<Timesheet> {
    if (timesheet) return timesheet;
    if (!selectedMemberId) throw new Error('No team member selected');
    const ts = await TimeService.getOrCreateTimesheet(selectedMemberId, formatDateISO(weekStart));
    setTimesheet(ts);
    return ts;
  }

  // ── Computed ────────────────────────────────────────────────
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const todayStr = formatDateISO(new Date());
  const isLocked = timesheet?.status === 'locked';
  const overheadNames = useMemo(() => overheadCategories.map((c) => c.name), [overheadCategories]);

  // Billable billing codes (no Z- prefix)
  const billableCodes = useMemo(
    () => activityCodes.filter((c) => !c.name.startsWith('Z-')),
    [activityCodes]
  );

  // Get project's current phase
  function getProjectPhase(projectId: string): string {
    const projPhases = allPhases
      .filter((ph) => ph.projectId === projectId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (projPhases.length === 0) return '';
    const inProgress = projPhases.filter((ph) => ph.status === 'in-progress');
    if (inProgress.length > 0) return inProgress[inProgress.length - 1].name;
    const complete = projPhases.filter((ph) => ph.status === 'complete');
    if (complete.length === projPhases.length) return projPhases[projPhases.length - 1].name;
    const pending = projPhases.filter((ph) => ph.status === 'pending');
    if (pending.length > 0) return pending[0].name;
    return projPhases[0].name;
  }

  // ── Hour Read/Write ─────────────────────────────────────────

  function getProjectHours(projectId: string, activityCodeId: string, date: Date): number {
    const ds = formatDateISO(date);
    return (
      entries.find(
        (e) =>
          e.projectId === projectId &&
          (e.activityCodeId || '') === activityCodeId &&
          e.date === ds &&
          !e.overheadCategory
      )?.hours || 0
    );
  }

  function getOverheadHours(category: string, date: Date): number {
    const ds = formatDateISO(date);
    return entries.find((e) => e.overheadCategory === category && e.date === ds)?.hours || 0;
  }

  // ── Vacation & holiday computed rows ──
  type VacationRow = { label: string; hoursByDate: Record<string, number>; total: number };
  const vacationRows = useMemo((): VacationRow[] => {
    const rows: VacationRow[] = [];

    // Approved vacation requests
    for (const req of vacationRequests) {
      const catName =
        vacationCategories.find((c) => c.id === req.vacationCategoryId)?.name || 'Vacation';
      const hoursByDate: Record<string, number> = {};
      let total = 0;
      for (const d of weekDates) {
        const ds = formatDateISO(d);
        const dow = d.getDay();
        if (dow > 0 && dow < 6 && ds >= req.startDate && ds <= req.endDate) {
          hoursByDate[ds] = req.hoursPerDay;
          total += req.hoursPerDay;
        }
      }
      if (total > 0) rows.push({ label: catName, hoursByDate, total });
    }

    // Firm holidays
    for (const h of firmHolidays) {
      const hoursByDate: Record<string, number> = {};
      let total = 0;
      for (const d of weekDates) {
        const ds = formatDateISO(d);
        if (ds === h.date) {
          hoursByDate[ds] = h.hours;
          total += h.hours;
        }
      }
      if (total > 0) rows.push({ label: `Holiday: ${h.name}`, hoursByDate, total });
    }

    return rows;
  }, [vacationRequests, firmHolidays, vacationCategories, weekDates]);

  const vacationDayTotals = weekDates.map((d) => {
    const ds = formatDateISO(d);
    return vacationRows.reduce((sum, row) => sum + (row.hoursByDate[ds] || 0), 0);
  });
  const vacationWeekTotal = vacationDayTotals.reduce((s, t) => s + t, 0);

  async function setProjectHours(row: ProjectRow, date: Date, hours: number) {
    if (isLocked) {
      toast('This timesheet is locked.', true);
      return;
    }
    const ds = formatDateISO(date);
    const existing = entries.find(
      (e) =>
        e.projectId === row.projectId &&
        (e.activityCodeId || '') === row.activityCodeId &&
        e.date === ds &&
        !e.overheadCategory
    );
    try {
      if (hours === 0 && existing) {
        await TimeService.deleteEntry(existing.id);
      } else if (hours > 0) {
        const ts = await ensureTimesheet();
        await TimeService.saveEntry({
          id: existing?.id || '',
          projectId: row.projectId,
          teamMemberId: selectedMemberId,
          date: ds,
          hours,
          billable: true,
          activityCodeId: row.activityCodeId || undefined,
          timesheetId: ts.id,
          overheadCategory: undefined,
          createdAt: '',
          updatedAt: '',
        });
      }
      await loadWeek();
    } catch (err: any) {
      toast('Error saving hours: ' + err.message, true);
    }
  }

  async function setOverheadHours(category: string, date: Date, hours: number) {
    if (isLocked) {
      toast('This timesheet is locked.', true);
      return;
    }
    const ds = formatDateISO(date);
    const existing = entries.find((e) => e.overheadCategory === category && e.date === ds);
    try {
      if (hours === 0 && existing) {
        await TimeService.deleteEntry(existing.id);
      } else if (hours > 0) {
        const ts = await ensureTimesheet();
        await TimeService.saveEntry({
          id: existing?.id || '',
          projectId: '',
          teamMemberId: selectedMemberId,
          date: ds,
          hours,
          billable: false,
          overheadCategory: category,
          timesheetId: ts.id,
          createdAt: '',
          updatedAt: '',
        });
      }
      await loadWeek();
    } catch (err: any) {
      toast('Error saving hours: ' + err.message, true);
    }
  }

  // ── Row Management ──────────────────────────────────────────
  function addProjectRow() {
    const firstProject = projects[0];
    if (!firstProject) {
      toast('No active projects available', true);
      return;
    }
    setProjectRows((prev) => [
      ...prev,
      { key: makeKey(), projectId: firstProject.id, phaseDesc: '', activityCodeId: '' },
    ]);
  }
  function removeProjectRow(key: string) {
    setProjectRows((prev) => prev.filter((r) => r.key !== key));
  }
  function updateProjectRow(key: string, patch: Partial<ProjectRow>) {
    setProjectRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  // ── Timesheet Actions ───────────────────────────────────────
  async function handleSubmit() {
    if (!timesheet || timesheet.status !== 'draft') return;
    try {
      const u = await TimeService.updateTimesheetStatus(
        timesheet.id,
        'submitted',
        selectedMemberId
      );
      setTimesheet(u);
      await loadWeek();
      toast('Timesheet submitted.');
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }
  function canSelfUnsubmit(ts: Timesheet | null): boolean {
    if (!ts || ts.status !== 'submitted' || !ts.submittedAt) return false;
    const elapsed = Date.now() - new Date(ts.submittedAt).getTime();
    return elapsed < 10 * 60 * 1000; // 10 minutes
  }

  async function handleUnsubmit() {
    if (!timesheet) return;
    if (!isAdmin && !canSelfUnsubmit(timesheet)) {
      toast('The 10-minute un-submit window has passed. Contact an admin to un-submit.', true);
      return;
    }
    try {
      const u = await TimeService.updateTimesheetStatus(timesheet.id, 'draft', selectedMemberId);
      setTimesheet(u);
      await loadWeek();
      toast('Timesheet returned to draft.');
    } catch (err: any) {
      toast('Unsubmit failed: ' + err.message, true);
    }
  }

  async function deleteTimesheet(tsId: string) {
    if (
      !confirm('Delete this timesheet? All associated hour entries for this week will be removed.')
    )
      return;
    try {
      await TimeService.deleteTimesheet(tsId);
      await loadWeek();
      toast('Timesheet deleted.');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }
  async function handleApprove() {
    if (!timesheet || !isAdmin || timesheet.status !== 'submitted') return;
    try {
      const u = await TimeService.updateTimesheetStatus(timesheet.id, 'approved', selectedMemberId);
      setTimesheet(u);
      await loadWeek();
      toast('Timesheet approved.');
    } catch (err: any) {
      toast('Approve failed: ' + err.message, true);
    }
  }
  async function handleLock() {
    if (!timesheet || !isAdmin || timesheet.status !== 'approved') return;
    try {
      const u = await TimeService.updateTimesheetStatus(timesheet.id, 'locked', selectedMemberId);
      setTimesheet(u);
      await loadWeek();
      toast('Timesheet locked.');
    } catch (err: any) {
      toast('Lock failed: ' + err.message, true);
    }
  }

  // ── Export ──────────────────────────────────────────────────
  function exportCSV() {
    const rows: string[] = [];
    rows.push(['Description', 'Billing Code', ...DAY_NAMES, 'Total'].join(','));
    // Overhead
    for (const cat of overheadNames) {
      const vals = weekDates.map((d) => getOverheadHours(cat, d) || '');
      const total = weekDates.reduce((s, d) => s + getOverheadHours(cat, d), 0);
      rows.push([`"${cat}"`, '"Non-billable"', ...vals, total].join(','));
    }
    // Projects
    for (const row of projectRows) {
      const proj = projects.find((p) => p.id === row.projectId);
      const code = activityCodes.find((c) => c.id === row.activityCodeId);
      const vals = weekDates.map(
        (d) => getProjectHours(row.projectId, row.activityCodeId, d) || ''
      );
      const total = weekDates.reduce(
        (s, d) => s + getProjectHours(row.projectId, row.activityCodeId, d),
        0
      );
      rows.push(
        [
          `"${proj?.projectNumber || ''} ${proj?.name || ''}"`,
          `"${code?.name || ''}"`,
          ...vals,
          total,
        ].join(',')
      );
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet-${formatDateISO(weekStart)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast('Timesheet exported as CSV');
  }

  function printTimesheet() {
    window.print();
  }

  // ── Totals ──────────────────────────────────────────────────
  const overheadDayTotals = weekDates.map((d) =>
    overheadNames.reduce((sum, cat) => sum + getOverheadHours(cat, d), 0)
  );
  const projectDayTotals = weekDates.map((d) =>
    projectRows.reduce((sum, row) => sum + getProjectHours(row.projectId, row.activityCodeId, d), 0)
  );
  const dayTotals = weekDates.map(
    (_, i) => overheadDayTotals[i] + projectDayTotals[i] + vacationDayTotals[i]
  );
  const weekTotal = dayTotals.reduce((s, t) => s + t, 0);
  const overheadWeekTotal = overheadDayTotals.reduce((s, t) => s + t, 0);
  const projectWeekTotal = projectDayTotals.reduce((s, t) => s + t, 0);
  function rowProjectTotal(row: ProjectRow): number {
    return weekDates.reduce(
      (sum, d) => sum + getProjectHours(row.projectId, row.activityCodeId, d),
      0
    );
  }
  function overheadCatTotal(cat: string): number {
    return weekDates.reduce((sum, d) => sum + getOverheadHours(cat, d), 0);
  }

  // Calculate total hours for a given timesheet from allTimesheets
  // We only know hours for the current week; sidebar shows status for others
  const currentWeekStr = formatDateISO(weekStart);

  // ── Render ──────────────────────────────────────────────────
  if (error)
    return (
      <div style={{ padding: 24, color: 'var(--dg)' }} role="alert">
        {error}
      </div>
    );

  const colWidthProject = 200;
  const colWidthPhase = 100;
  const colWidthActivity = 130;
  const colWidthDay = 62;
  const colWidthTotal = 56;
  const colWidthRemove = 34;

  const headerTh = (label: string, sub?: string, isAccent?: boolean) => (
    <th
      key={label + (sub || '')}
      style={{
        ...S.thCell,
        color: isAccent ? 'var(--ac)' : 'var(--tm)',
        background: isAccent ? 'rgba(var(--acr),0.06)' : undefined,
      }}
    >
      <div>{label}</div>
      {sub && <div style={{ fontSize: 10, fontWeight: 400, marginTop: 1 }}>{sub}</div>}
    </th>
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* ═══ LEFT SIDEBAR — Timesheet History ═══ */}
      <div
        style={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid var(--bd)',
          background: 'var(--sf)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 14px 10px', borderBottom: '1px solid var(--bd)' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.05em',
              marginBottom: 4,
            }}
          >
            My Timesheets
          </div>
          <div style={{ fontSize: 11, color: 'var(--tm)' }}>
            {members.find((m) => m.id === selectedMemberId)?.name || ''}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {allTimesheets.length === 0 && (
            <div
              style={{
                padding: '20px 14px',
                textAlign: 'center',
                color: 'var(--tm)',
                fontSize: 11,
              }}
            >
              No timesheets yet
            </div>
          )}
          {allTimesheets.map((ts) => {
            const ws = new Date(ts.weekStart + 'T00:00:00');
            const isSelected = ts.weekStart === currentWeekStr;
            const sc = STATUS_COLORS[ts.status] || STATUS_COLORS.draft;
            return (
              <div
                key={ts.id}
                onClick={() => setWeekStart(ws)}
                style={{
                  padding: '8px 14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--bd)',
                  background: isSelected ? 'rgba(var(--acr),0.08)' : undefined,
                  borderLeft: isSelected ? '3px solid var(--ac)' : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'rgba(var(--acr),0.04)';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = '';
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {formatDateShort(ws)} — {formatDateShort(addDays(ws, 6))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: sc.bg,
                        color: sc.color,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.03em',
                      }}
                    >
                      {sc.label}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTimesheet(ts.id);
                      }}
                      title="Delete timesheet"
                      aria-label="Delete timesheet"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--dg)',
                        cursor: 'pointer',
                        padding: 4,
                        opacity: 0.6,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 2 }}>
                  {ws.getFullYear()}
                </div>
              </div>
            );
          })}
        </div>
        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--bd)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)' }}>
            Week starting:
          </label>
          <input
            type="date"
            defaultValue={formatDateISO(getMonday(new Date()))}
            id="ts-new-week-picker"
            style={{
              padding: '5px 8px',
              borderRadius: 4,
              border: '1px solid var(--bd)',
              background: 'var(--ib)',
              color: 'var(--tx)',
              fontSize: 13,
            }}
          />
          <button
            onClick={async () => {
              if (!selectedMemberId) {
                toast('Still loading — please wait.', true);
                return;
              }
              const input = document.getElementById('ts-new-week-picker') as HTMLInputElement;
              const picked = input?.value ? new Date(input.value + 'T00:00:00') : new Date();
              const monday = getMonday(picked);
              const weekStr = formatDateISO(monday);
              const exists = allTimesheets.find((t) => t.weekStart === weekStr);
              if (exists) {
                setWeekStart(monday);
                toast('Timesheet already exists for this week.');
                return;
              }
              try {
                await TimeService.getOrCreateTimesheet(selectedMemberId, weekStr);
                setWeekStart(monday);
                await loadWeek();
                toast('Timesheet created.');
              } catch (err: any) {
                toast('Failed: ' + err.message, true);
              }
            }}
            style={S.btnPrimary()}
          >
            + Add Timesheet
          </button>
        </div>
      </div>

      {/* ═══ MAIN CONTENT — Timesheet Grid ═══ */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {/* Header Row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700 }}>
            {formatDateShort(weekStart)} — {formatDateShort(addDays(weekStart, 6))},{' '}
            {weekStart.getFullYear()}
          </span>
          {timesheet && <StatusBadge status={timesheet.status} />}
          {timesheet && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <Button onClick={exportCSV} variant="outline" size="sm" title="Export as CSV">
                Export
              </Button>
              <Button onClick={printTimesheet} variant="outline" size="sm" title="Print">
                Print
              </Button>
              {timesheet.status === 'draft' && (
                <Button onClick={handleSubmit} variant="default" size="sm">
                  Submit
                </Button>
              )}
              {timesheet.status === 'submitted' && (isAdmin || canSelfUnsubmit(timesheet)) && (
                <Button onClick={handleUnsubmit} variant="outline" size="sm">
                  Unsubmit
                </Button>
              )}
              {timesheet.status === 'submitted' && isAdmin && (
                <Button onClick={handleApprove} variant="default" size="sm">
                  Approve
                </Button>
              )}
              {timesheet.status === 'approved' && isAdmin && (
                <Button onClick={handleUnsubmit} variant="outline" size="sm">
                  Unsubmit
                </Button>
              )}
              {timesheet.status === 'approved' && isAdmin && (
                <Button onClick={handleLock} variant="destructive" size="sm">
                  Lock
                </Button>
              )}
            </div>
          )}
        </div>

        {!timesheet && (
          <EmptyState
            title="No timesheet selected"
            subtitle='Select a timesheet from the sidebar or click "+ Add Timesheet" to get started.'
          />
        )}

        {/* Timesheet Table */}
        {timesheet && (
          <>
            <div
              style={{
                background: 'var(--sf)',
                borderRadius: 10,
                border: '1px solid var(--bd)',
                overflow: 'hidden',
                marginBottom: 16,
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: colWidthProject }} />
                  <col style={{ width: colWidthPhase }} />
                  <col style={{ width: colWidthActivity }} />
                  {weekDates.map((_, i) => (
                    <col key={i} style={{ width: colWidthDay }} />
                  ))}
                  <col style={{ width: colWidthTotal }} />
                  <col style={{ width: colWidthRemove }} />
                </colgroup>
                <thead>
                  <tr style={{ background: 'var(--sa)' }}>
                    <th style={{ ...S.thCell, textAlign: 'left', padding: '9px 12px' }}>
                      Description
                    </th>
                    <th style={{ ...S.thCell, textAlign: 'left', padding: '9px 6px' }}>Phase</th>
                    <th style={{ ...S.thCell, textAlign: 'left', padding: '9px 8px' }}>
                      Billing Code
                    </th>
                    {weekDates.map((d, i) => {
                      const isToday = formatDateISO(d) === todayStr;
                      return headerTh(DAY_NAMES[i], `${d.getMonth() + 1}/${d.getDate()}`, isToday);
                    })}
                    <th style={{ ...S.thCell }}>TOTAL</th>
                    <th style={{ width: colWidthRemove, borderBottom: '1px solid var(--bd)' }} />
                  </tr>
                </thead>
                <tbody>
                  {/* ── VACATION & HOLIDAYS (auto-populated, read-only) ── */}
                  {vacationRows.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={12} style={S.sectionLabel}>
                          Vacation &amp; Holidays
                        </td>
                      </tr>
                      {vacationRows.map((row, ri) => (
                        <tr
                          key={`vac-${ri}`}
                          style={{
                            borderBottom: '1px solid var(--bd)',
                            background: 'rgba(var(--okr),0.03)',
                          }}
                        >
                          <td
                            style={{
                              padding: '5px 12px',
                              fontSize: 13,
                              fontStyle: 'italic',
                              color: 'var(--ok)',
                            }}
                          >
                            {row.label}
                          </td>
                          <td style={{ padding: '5px 6px' }}></td>
                          <td
                            style={{
                              padding: '5px 8px',
                              fontSize: 10,
                              color: 'var(--tm)',
                              fontStyle: 'italic',
                            }}
                          >
                            Non-billable
                          </td>
                          {weekDates.map((d, di) => {
                            const ds = formatDateISO(d);
                            const isToday = ds === todayStr;
                            const hrs = row.hoursByDate[ds] || 0;
                            return (
                              <td
                                key={di}
                                style={{
                                  padding: '3px 3px',
                                  textAlign: 'center',
                                  background: isToday ? 'rgba(var(--acr),0.03)' : undefined,
                                }}
                              >
                                <span
                                  style={{
                                    display: 'block',
                                    padding: '4px 0',
                                    fontSize: 13,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    color: hrs > 0 ? 'var(--ok)' : 'var(--tm)',
                                  }}
                                >
                                  {hrs || '–'}
                                </span>
                              </td>
                            );
                          })}
                          <td
                            style={{
                              padding: '5px 6px',
                              textAlign: 'center',
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 13,
                              fontWeight: 600,
                              color: row.total > 0 ? 'var(--ok)' : 'var(--tm)',
                            }}
                          >
                            {row.total || '–'}
                          </td>
                          <td />
                        </tr>
                      ))}
                      {/* Vacation subtotal */}
                      <tr
                        style={{
                          background: 'rgba(var(--okr),0.04)',
                          borderBottom: '2px solid var(--bd)',
                        }}
                      >
                        <td
                          colSpan={3}
                          style={{
                            padding: '7px 12px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'var(--ok)',
                          }}
                        >
                          VACATION SUBTOTAL
                        </td>
                        {vacationDayTotals.map((t, i) => (
                          <td
                            key={i}
                            style={{
                              padding: '7px 4px',
                              textAlign: 'center',
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 13,
                              fontWeight: 700,
                              color: t > 0 ? 'var(--ok)' : 'var(--bd)',
                            }}
                          >
                            {t || '–'}
                          </td>
                        ))}
                        <td
                          style={{
                            padding: '7px 6px',
                            textAlign: 'center',
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            fontWeight: 700,
                            color: vacationWeekTotal > 0 ? 'var(--ok)' : 'var(--bd)',
                          }}
                        >
                          {vacationWeekTotal || '–'}
                        </td>
                        <td />
                      </tr>
                    </>
                  )}

                  {/* ── OVERHEAD SECTION ── */}
                  <tr>
                    <td colSpan={12} style={S.sectionLabel}>
                      Overhead / Non-Billable Hours
                    </td>
                  </tr>
                  {overheadNames.map((cat) => {
                    const catTotal = overheadCatTotal(cat);
                    return (
                      <tr key={cat} style={{ borderBottom: '1px solid var(--bd)' }}>
                        <td style={{ padding: '5px 12px', fontSize: 13 }}>{cat}</td>
                        <td style={{ padding: '5px 6px', fontSize: 10, color: 'var(--tm)' }}></td>
                        <td
                          style={{
                            padding: '5px 8px',
                            fontSize: 11,
                            color: 'var(--tm)',
                            fontStyle: 'italic',
                          }}
                        >
                          Non-billable
                        </td>
                        {weekDates.map((d, di) => {
                          const isToday = formatDateISO(d) === todayStr;
                          const hrs = getOverheadHours(cat, d);
                          return (
                            <td
                              key={di}
                              style={{
                                padding: '3px 3px',
                                background: isToday ? 'rgba(var(--acr),0.03)' : undefined,
                              }}
                            >
                              <input
                                type="number"
                                min="0"
                                max="24"
                                step="0.25"
                                value={hrs || ''}
                                onChange={(e) =>
                                  setOverheadHours(cat, d, parseFloat(e.target.value) || 0)
                                }
                                placeholder="–"
                                disabled={isLocked}
                                style={{
                                  ...S.hourInput,
                                  background: hrs > 0 ? 'rgba(var(--acr),0.06)' : 'var(--ib)',
                                  opacity: isLocked ? 0.5 : 1,
                                }}
                              />
                            </td>
                          );
                        })}
                        <td
                          style={{
                            padding: '5px 6px',
                            textAlign: 'center',
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            fontWeight: 600,
                            color: catTotal > 0 ? 'var(--tx)' : 'var(--tm)',
                          }}
                        >
                          {catTotal || '–'}
                        </td>
                        <td />
                      </tr>
                    );
                  })}
                  {/* Overhead subtotal */}
                  <tr
                    style={{
                      background: 'rgba(var(--acr),0.025)',
                      borderBottom: '2px solid var(--bd)',
                    }}
                  >
                    <td
                      colSpan={3}
                      style={{
                        padding: '7px 12px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--tm)',
                      }}
                    >
                      OVERHEAD SUBTOTAL
                    </td>
                    {overheadDayTotals.map((t, i) => (
                      <td
                        key={i}
                        style={{
                          padding: '7px 4px',
                          textAlign: 'center',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 13,
                          fontWeight: 700,
                          color: t > 0 ? 'var(--tm)' : 'var(--bd)',
                        }}
                      >
                        {t || '–'}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: '7px 6px',
                        textAlign: 'center',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 13,
                        fontWeight: 700,
                        color: overheadWeekTotal > 0 ? 'var(--tm)' : 'var(--bd)',
                      }}
                    >
                      {overheadWeekTotal || '–'}
                    </td>
                    <td />
                  </tr>

                  {/* ── PROJECT HOURS SECTION ── */}
                  <tr>
                    <td colSpan={12} style={S.sectionLabel}>
                      Project Hours
                    </td>
                  </tr>
                  {projectRows.map((row) => {
                    const rowTotal = rowProjectTotal(row);
                    const phase = getProjectPhase(row.projectId);
                    return (
                      <tr key={row.key} style={{ borderBottom: '1px solid var(--bd)' }}>
                        <td style={{ padding: '4px 6px 4px 10px' }}>
                          <ProjectPicker
                            value={row.projectId}
                            projects={projects}
                            onChange={(id) => updateProjectRow(row.key, { projectId: id })}
                            disabled={isLocked}
                          />
                        </td>
                        <td style={{ padding: '4px 4px', fontSize: 10, color: 'var(--tm)' }}>
                          {phase || '–'}
                        </td>
                        <td style={{ padding: '4px 4px' }}>
                          <SearchableSelect
                            value={row.activityCodeId}
                            options={billableCodes.map((c) => ({ value: c.id, label: c.name }))}
                            onChange={(v) => updateProjectRow(row.key, { activityCodeId: v })}
                            disabled={isLocked}
                            placeholder="-- none --"
                            searchPlaceholder="Search activity codes…"
                            style={{ fontSize: 11, padding: '5px 6px' }}
                          />
                        </td>
                        {weekDates.map((d, di) => {
                          const isToday = formatDateISO(d) === todayStr;
                          const hrs = getProjectHours(row.projectId, row.activityCodeId, d);
                          // Forecast suggestion for this cell — surfaces in the
                          // input placeholder so the user can see "you forecast
                          // 8h here" and click to adopt. Skip vacation-source
                          // forecasts (those render in their own locked section).
                          const ds = formatDateISO(d);
                          const forecastForCell = forecasts.find(
                            (f) =>
                              f.projectId === row.projectId &&
                              f.dayDate === ds &&
                              f.sourceKind !== 'vacation'
                          );
                          const placeholder = forecastForCell ? `${forecastForCell.hours}` : '–';
                          return (
                            <td
                              key={di}
                              style={{
                                padding: '3px 3px',
                                background: isToday ? 'rgba(var(--acr),0.03)' : undefined,
                                position: 'relative',
                              }}
                            >
                              <input
                                type="number"
                                min="0"
                                max="24"
                                step="0.25"
                                value={hrs || ''}
                                onChange={(e) =>
                                  setProjectHours(row, d, parseFloat(e.target.value) || 0)
                                }
                                onDoubleClick={() => {
                                  // Double-click adopts the forecast value into the cell.
                                  if (forecastForCell && hrs === 0 && !isLocked && row.projectId) {
                                    setProjectHours(row, d, forecastForCell.hours);
                                  }
                                }}
                                placeholder={placeholder}
                                disabled={isLocked || !row.projectId}
                                title={
                                  forecastForCell && hrs === 0
                                    ? `Forecast suggests ${forecastForCell.hours}h. Double-click to adopt.`
                                    : undefined
                                }
                                style={{
                                  ...S.hourInput,
                                  background: hrs > 0 ? 'rgba(var(--acr),0.06)' : 'var(--ib)',
                                  opacity: isLocked ? 0.5 : 1,
                                  // Faded-color placeholder hint for a forecast suggestion.
                                  color:
                                    hrs > 0
                                      ? 'var(--tx)'
                                      : forecastForCell
                                        ? 'var(--tm)'
                                        : 'var(--tx)',
                                  fontStyle: hrs === 0 && forecastForCell ? 'italic' : 'normal',
                                }}
                              />
                            </td>
                          );
                        })}
                        <td
                          style={{
                            padding: '5px 6px',
                            textAlign: 'center',
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            fontWeight: 600,
                            color: rowTotal > 0 ? 'var(--tx)' : 'var(--tm)',
                          }}
                        >
                          {rowTotal || '–'}
                        </td>
                        <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                          {!isLocked && (
                            <button
                              onClick={() => removeProjectRow(row.key)}
                              title="Remove row"
                              aria-label="Remove row"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--dg)',
                                cursor: 'pointer',
                                padding: 4,
                                opacity: 0.7,
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <X size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Add row button */}
                  {!isLocked && (
                    <tr>
                      <td colSpan={12} style={{ padding: '7px 12px' }}>
                        <button
                          onClick={addProjectRow}
                          style={{
                            padding: '5px 12px',
                            borderRadius: 4,
                            border: '1px dashed var(--bd)',
                            background: 'transparent',
                            color: 'var(--ac)',
                            fontSize: 13,
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        >
                          + Add Project Row
                        </button>
                      </td>
                    </tr>
                  )}
                  {/* Project subtotal */}
                  <tr
                    style={{
                      background: 'rgba(var(--acr),0.025)',
                      borderBottom: '2px solid var(--bd)',
                    }}
                  >
                    <td
                      colSpan={3}
                      style={{
                        padding: '7px 12px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--tm)',
                      }}
                    >
                      PROJECT SUBTOTAL
                    </td>
                    {projectDayTotals.map((t, i) => (
                      <td
                        key={i}
                        style={{
                          padding: '7px 4px',
                          textAlign: 'center',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 13,
                          fontWeight: 700,
                          color: t > 0 ? 'var(--tm)' : 'var(--bd)',
                        }}
                      >
                        {t || '–'}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: '7px 6px',
                        textAlign: 'center',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 13,
                        fontWeight: 700,
                        color: projectWeekTotal > 0 ? 'var(--tm)' : 'var(--bd)',
                      }}
                    >
                      {projectWeekTotal || '–'}
                    </td>
                    <td />
                  </tr>
                  {/* Daily totals row */}
                  <tr style={{ background: 'var(--sa)', fontWeight: 700 }}>
                    <td
                      colSpan={3}
                      style={{ padding: '10px 12px', fontSize: 13, color: 'var(--tm)' }}
                    >
                      DAILY TOTAL
                    </td>
                    {dayTotals.map((t, i) => (
                      <td
                        key={i}
                        style={{
                          padding: '10px 4px',
                          textAlign: 'center',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 13,
                          color: t > 8 ? 'var(--dg)' : t > 0 ? 'var(--tx)' : 'var(--tm)',
                        }}
                      >
                        {t || '–'}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: '10px 6px',
                        textAlign: 'center',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 16,
                        fontWeight: 700,
                        color: weekTotal > 40 ? 'var(--dg)' : 'var(--ac)',
                      }}
                    >
                      {weekTotal}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: 14 }}>
              {[
                {
                  label: 'WEEK TOTAL',
                  value: `${weekTotal}h`,
                  color: weekTotal > 40 ? 'var(--dg)' : 'var(--ac)',
                },
                { label: 'PROJECT HOURS', value: `${projectWeekTotal}h`, color: undefined },
                { label: 'OVERHEAD HOURS', value: `${overheadWeekTotal}h`, color: undefined },
                {
                  label: 'AVG / DAY (5d)',
                  value: `${weekTotal > 0 ? (weekTotal / 5).toFixed(1) : '0.0'}h`,
                  color: undefined,
                },
              ].map((c) => (
                <div
                  key={c.label}
                  style={{
                    background: 'var(--sf)',
                    borderRadius: 8,
                    border: '1px solid var(--bd)',
                    padding: '12px 16px',
                    flex: 1,
                  }}
                >
                  <div
                    style={{ fontSize: 11, color: 'var(--tm)', fontWeight: 600, marginBottom: 4 }}
                  >
                    {c.label}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: c.color || 'var(--tx)',
                    }}
                  >
                    {c.value}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EXPENSES PAGE
// ═══════════════════════════════════════════════════════════════

function ExpensesPage({ toast, authUser }: PageProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Record<string, ExpenseAttachment[]>>({});

  // Load base data
  useEffect(() => {
    loadBase();
  }, []);
  useEffect(() => {
    const handler = () => loadBase();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);
  useEffect(() => {
    if (selectedMemberId) loadExpenses();
  }, [selectedMemberId]);

  async function loadBase() {
    try {
      const [memsR, projsR, etsR] = await Promise.allSettled([
        TeamService.list(),
        ProjectService.list(),
        AdminService.expenseTypes.list(),
      ]);
      const mems = settled(memsR, [] as TeamMember[]);
      const projs = settled(projsR, [] as Project[]);
      const ets = settled(etsR, [] as ExpenseType[]);
      const active = mems.filter((m) => m.active);
      setMembers(active);
      setProjects(projs.filter((p) => p.status === 'active'));
      setExpenseTypes(ets);

      // Find logged-in user's team member record
      const myId = authUser?.teamMemberId || authUser?.id;
      const me = myId ? active.find((m) => m.id === myId) : null;
      const selectedMember = me || active[0];

      // Admin-over-expenses = can view or approve everyone else's.
      const isExpenseAdmin =
        hasPermission(authUser?.permissions, 'expenses.view_all') ||
        hasPermission(authUser?.permissions, 'expenses.approve');
      if (selectedMember) {
        setSelectedMemberId(selectedMember.id);
        setIsAdmin(isExpenseAdmin);
      }
    } catch (err: any) {
      toast('Failed to load data: ' + err.message, true);
    }
  }

  async function loadExpenses() {
    if (!selectedMemberId) return;
    try {
      const list = await ExpenseService.list(selectedMemberId);
      setExpenses(list);
      // Load attachments for all expenses
      const attMap: Record<string, ExpenseAttachment[]> = {};
      for (const exp of list) {
        try {
          attMap[exp.id] = await ExpenseService.listAttachments(exp.id);
        } catch {
          attMap[exp.id] = [];
        }
      }
      setAttachments(attMap);
    } catch (err: any) {
      toast('Failed to load expenses: ' + err.message, true);
    }
  }

  // 10-minute un-submit window
  function canSelfUnsubmit(exp: Expense): boolean {
    if (exp.status !== 'submitted' || !exp.submittedAt) return false;
    const elapsed = Date.now() - new Date(exp.submittedAt).getTime();
    return elapsed < 10 * 60 * 1000;
  }

  // Add new expense row
  async function addExpense() {
    const today = new Date();
    const dateStr = formatDateISO(today);
    const monthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const newExp: Expense = {
      id: '',
      teamMemberId: selectedMemberId,
      projectId: projects[0]?.id || undefined,
      expenseTypeId: expenseTypes[0]?.id || '',
      date: dateStr,
      quantity: 0,
      total: 0,
      comment: '',
      month: monthStr,
      status: 'draft',
      createdAt: '',
      updatedAt: '',
    };
    try {
      const saved = await ExpenseService.save(newExp);
      setSelectedExpenseId(saved.id);
      setEditingId(saved.id);
      await loadExpenses();
    } catch (err: any) {
      toast('Failed to add expense: ' + err.message, true);
    }
  }

  async function saveExpense(exp: Expense) {
    try {
      // Recalculate total based on expense type rate
      const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
      const rate = et?.rate ?? undefined;
      const total = rate != null ? exp.quantity * rate : exp.quantity;
      // Keep month in sync with the date
      const month = exp.date ? exp.date.substring(0, 7) : exp.month;
      await ExpenseService.save({ ...exp, rate, total, month });
      await loadExpenses();
    } catch (err: any) {
      toast('Failed to save: ' + err.message, true);
    }
  }

  async function deleteExpense(id: string) {
    try {
      await ExpenseService.delete(id);
      if (selectedExpenseId === id) setSelectedExpenseId(null);
      await loadExpenses();
    } catch (err: any) {
      toast('Failed to delete: ' + err.message, true);
    }
  }

  async function handleSubmitExpense(id: string) {
    try {
      await ExpenseService.updateStatus([id], 'submitted', selectedMemberId);
      toast('Expense submitted.');
      await loadExpenses();
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }

  async function handleUnsubmitExpense(id: string) {
    const exp = expenses.find((e) => e.id === id);
    if (!exp) return;
    if (!isAdmin && !canSelfUnsubmit(exp)) {
      toast('Un-submit window has expired (10 minutes). Contact an admin.', true);
      return;
    }
    try {
      await ExpenseService.updateStatus([id], 'draft', selectedMemberId);
      toast('Expense returned to draft.');
      await loadExpenses();
    } catch (err: any) {
      toast('Unsubmit failed: ' + err.message, true);
    }
  }

  async function handleSubmitAll() {
    const draftIds = expenses.filter((e) => e.status === 'draft').map((e) => e.id);
    if (draftIds.length === 0) {
      toast('No draft expenses to submit.', true);
      return;
    }
    try {
      await ExpenseService.updateStatus(draftIds, 'submitted', selectedMemberId);
      toast(`${draftIds.length} expense(s) submitted.`);
      await loadExpenses();
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }

  async function handleApproveAll() {
    const submittedIds = expenses.filter((e) => e.status === 'submitted').map((e) => e.id);
    if (submittedIds.length === 0) return;
    try {
      await ExpenseService.updateStatus(submittedIds, 'approved', selectedMemberId);
      toast(`${submittedIds.length} expense(s) approved.`);
      await loadExpenses();
    } catch (err: any) {
      toast('Approve failed: ' + err.message, true);
    }
  }

  async function handleAttach(expenseId: string) {
    try {
      const file = await ExpenseService.openFileDialog();
      if (!file) return;
      await ExpenseService.saveAttachment({
        id: '',
        expenseId,
        fileName: file.fileName,
        mimeType: file.mimeType,
        data: file.data,
        createdAt: '',
      });
      const atts = await ExpenseService.listAttachments(expenseId);
      setAttachments((prev) => ({ ...prev, [expenseId]: atts }));
      toast('Attachment added.');
    } catch (err: any) {
      toast('Attach failed: ' + err.message, true);
    }
  }

  async function handleDeleteAttachment(attId: string, expenseId: string) {
    try {
      await ExpenseService.deleteAttachment(attId);
      const atts = await ExpenseService.listAttachments(expenseId);
      setAttachments((prev) => ({ ...prev, [expenseId]: atts }));
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  function exportCSV() {
    const rows: string[] = [];
    rows.push(
      ['Date', 'Project', 'Expense Type', 'Quantity', 'Rate', 'Total', 'Comment', 'Status'].join(
        ','
      )
    );
    for (const exp of expenses) {
      const proj = projects.find((p) => p.id === exp.projectId);
      const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
      rows.push(
        [
          exp.date,
          `"${proj?.name || ''}"`,
          `"${et?.name || ''}"`,
          exp.quantity,
          exp.rate ?? '',
          exp.total.toFixed(2),
          `"${(exp.comment || '').replace(/"/g, '""')}"`,
          exp.status,
        ].join(',')
      );
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses-${selectedMemberId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast('Expenses exported as CSV');
  }

  const grandTotal = expenses.reduce((s, e) => s + e.total, 0);
  const draftCount = expenses.filter((e) => e.status === 'draft').length;
  const submittedCount = expenses.filter((e) => e.status === 'submitted').length;

  const inputStyle: React.CSSProperties = {
    padding: '5px 6px',
    borderRadius: 4,
    border: '1px solid var(--bd)',
    background: 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };
  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* ═══ LEFT SIDEBAR — Expense History ═══ */}
      <div
        style={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid var(--bd)',
          background: 'var(--sf)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 14px 10px', borderBottom: '1px solid var(--bd)' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.05em',
              marginBottom: 8,
            }}
          >
            Expense Reports
          </div>
          <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 8 }}>
            {members.find((m) => m.id === selectedMemberId)?.name || ''}
          </div>
          <button
            onClick={addExpense}
            style={{
              width: '100%',
              padding: '7px 10px',
              borderRadius: 6,
              border: '1px dashed var(--bd)',
              background: 'transparent',
              color: 'var(--ac)',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            + New Expense
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {expenses.length === 0 && (
            <div
              style={{
                padding: '20px 14px',
                textAlign: 'center',
                color: 'var(--tm)',
                fontSize: 13,
              }}
            >
              No expense reports yet.
            </div>
          )}
          {expenses.map((exp) => {
            const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
            const sc = STATUS_COLORS[exp.status] || STATUS_COLORS.draft;
            const isSel = exp.id === selectedExpenseId;
            const dateLabel = exp.date
              ? new Date(exp.date + 'T00:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'No date';
            return (
              <div
                key={exp.id}
                onClick={() => setSelectedExpenseId(exp.id)}
                style={{
                  padding: '8px 14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--bd)',
                  background: isSel ? 'rgba(var(--acr),0.08)' : undefined,
                  borderLeft: isSel ? '3px solid var(--ac)' : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSel) e.currentTarget.style.background = 'rgba(var(--acr),0.04)';
                }}
                onMouseLeave={(e) => {
                  if (!isSel) e.currentTarget.style.background = '';
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{dateLabel}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: sc.bg,
                        color: sc.color,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.03em',
                      }}
                    >
                      {sc.label}
                    </span>
                    {exp.status === 'draft' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteExpense(exp.id);
                        }}
                        title="Delete expense"
                        aria-label="Delete expense"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--dg)',
                          cursor: 'pointer',
                          padding: 4,
                          opacity: 0.6,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 2,
                  }}
                >
                  <span style={{ fontSize: 11, color: 'var(--tm)' }}>
                    {et?.name || 'Unknown type'}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: exp.total > 0 ? 'var(--tx)' : 'var(--tm)',
                    }}
                  >
                    ${exp.total.toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700 }}>Expense Reports</span>
          <span style={{ fontSize: 13, color: 'var(--tm)' }}>{expenses.length} total</span>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <Button onClick={exportCSV} variant="outline" size="sm" title="Export as CSV">
              Export
            </Button>
            <Button onClick={() => window.print()} variant="outline" size="sm" title="Print">
              Print
            </Button>
            {draftCount > 0 && (
              <Button onClick={handleSubmitAll} variant="default" size="sm">
                Submit All ({draftCount})
              </Button>
            )}
            {submittedCount > 0 && isAdmin && (
              <Button onClick={handleApproveAll} variant="outline" size="sm">
                Approve All ({submittedCount})
              </Button>
            )}
          </div>
        </div>

        {/* Expense Table */}
        <div style={{ marginBottom: 16 }}>
          <DataTable<Expense>
            columns={[
              {
                key: 'date',
                label: 'Date',
                width: 100,
                render: (exp) => {
                  const isEditable = exp.status === 'draft';
                  return (
                    <input
                      type="date"
                      value={exp.date}
                      disabled={!isEditable}
                      onChange={(e) => saveExpense({ ...exp, date: e.target.value })}
                      style={{ ...inputStyle, opacity: isEditable ? 1 : 0.6 }}
                    />
                  );
                },
                getValue: (exp) => exp.date,
              },
              {
                key: 'project',
                label: 'Project',
                width: 160,
                render: (exp) => {
                  const isEditable = exp.status === 'draft';
                  return (
                    <SearchableSelect
                      value={exp.projectId || ''}
                      options={projects.map((p) => ({
                        value: p.id,
                        label: p.name,
                        hint: p.projectNumber || undefined,
                      }))}
                      onChange={(v) => saveExpense({ ...exp, projectId: v || undefined })}
                      disabled={!isEditable}
                      placeholder="-- none --"
                      searchPlaceholder="Search projects…"
                      style={{ ...selectStyle, opacity: isEditable ? 1 : 0.6 }}
                    />
                  );
                },
                getValue: (exp) => {
                  const p = projects.find((p) => p.id === exp.projectId);
                  return p ? (p.projectNumber ? `${p.projectNumber} - ${p.name}` : p.name) : '';
                },
              },
              {
                key: 'expenseType',
                label: 'Expense Type',
                width: 130,
                render: (exp) => {
                  const isEditable = exp.status === 'draft';
                  return (
                    <SearchableSelect
                      value={exp.expenseTypeId}
                      options={expenseTypes.map((t) => ({ value: t.id, label: t.name }))}
                      onChange={(v) => saveExpense({ ...exp, expenseTypeId: v })}
                      disabled={!isEditable}
                      searchPlaceholder="Search expense types…"
                      clearable={false}
                      style={{ ...selectStyle, opacity: isEditable ? 1 : 0.6 }}
                    />
                  );
                },
                getValue: (exp) => expenseTypes.find((t) => t.id === exp.expenseTypeId)?.name || '',
              },
              {
                key: 'quantity',
                label: 'Qty / Amt',
                width: 80,
                render: (exp) => {
                  const isEditable = exp.status === 'draft';
                  return (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={exp.quantity || ''}
                      disabled={!isEditable}
                      onChange={(e) =>
                        saveExpense({ ...exp, quantity: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="0.00"
                      style={{
                        ...inputStyle,
                        textAlign: 'right',
                        fontFamily: "'JetBrains Mono', monospace",
                        opacity: isEditable ? 1 : 0.6,
                      }}
                    />
                  );
                },
                getValue: (exp) => exp.quantity || 0,
              },
              {
                key: 'rate',
                label: 'Rate',
                width: 70,
                render: (exp) => {
                  const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
                  return (
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--tm)',
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {et?.rate != null ? `$${et.rate.toFixed(2)}` : 'N/A'}
                    </span>
                  );
                },
                getValue: (exp) => {
                  const et = expenseTypes.find((t) => t.id === exp.expenseTypeId);
                  return et?.rate ?? 0;
                },
              },
              {
                key: 'total',
                label: 'Total',
                width: 80,
                render: (exp) => (
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: exp.total > 0 ? 'var(--tx)' : 'var(--tm)',
                    }}
                  >
                    ${exp.total.toFixed(2)}
                  </span>
                ),
                getValue: (exp) => exp.total,
              },
              {
                key: 'comment',
                label: 'Comment',
                render: (exp) => {
                  const isEditable = exp.status === 'draft';
                  return (
                    <input
                      type="text"
                      value={exp.comment || ''}
                      disabled={!isEditable}
                      onChange={(e) => saveExpense({ ...exp, comment: e.target.value })}
                      placeholder="Notes..."
                      style={{ ...inputStyle, opacity: isEditable ? 1 : 0.6 }}
                    />
                  );
                },
                getValue: (exp) => exp.comment || '',
              },
              {
                key: 'files',
                label: 'Files',
                width: 80,
                sortable: false,
                render: (exp) => {
                  const isEditable = exp.status === 'draft';
                  const expAtts = attachments[exp.id] || [];
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        justifyContent: 'center',
                      }}
                    >
                      {expAtts.length > 0 && (
                        <span style={{ fontSize: 10, color: 'var(--ac)', fontWeight: 600 }}>
                          {expAtts.length}
                        </span>
                      )}
                      {isEditable && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAttach(exp.id);
                          }}
                          title="Attach file"
                          aria-label="Attach file"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--ac)',
                            cursor: 'pointer',
                            padding: 4,
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          <Plus size={14} />
                        </button>
                      )}
                      {expAtts.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(editingId === exp.id ? null : exp.id);
                          }}
                          title="View attachments"
                          aria-label={
                            editingId === exp.id ? 'Hide attachments' : 'View attachments'
                          }
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--tm)',
                            cursor: 'pointer',
                            padding: 4,
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          {editingId === exp.id ? (
                            <ChevronUp size={12} />
                          ) : (
                            <ChevronDown size={12} />
                          )}
                        </button>
                      )}
                    </div>
                  );
                },
              },
              {
                key: 'status',
                label: 'Status',
                width: 70,
                render: (exp) => {
                  const sc = STATUS_COLORS[exp.status] || STATUS_COLORS.draft;
                  return (
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: sc.bg,
                        color: sc.color,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.03em',
                      }}
                    >
                      {sc.label}
                    </span>
                  );
                },
                getValue: (exp) => exp.status,
              },
            ]}
            data={expenses}
            keyField="id"
            storageKey="expenses-table"
            emptyTitle="No Expenses"
            emptyMessage='No expenses yet. Click "+ New Expense" to get started.'
            onRowClick={(exp) => setSelectedExpenseId(exp.id)}
            actions={(exp) => {
              const isEditable = exp.status === 'draft';
              return (
                <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  {isEditable && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmitExpense(exp.id);
                      }}
                      title="Submit"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--ac)',
                        cursor: 'pointer',
                        fontSize: 10,
                        padding: '2px 4px',
                        fontWeight: 700,
                      }}
                    >
                      Submit
                    </button>
                  )}
                  {exp.status === 'submitted' && (isAdmin || canSelfUnsubmit(exp)) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnsubmitExpense(exp.id);
                      }}
                      title="Unsubmit"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-warning)',
                        cursor: 'pointer',
                        fontSize: 10,
                        padding: '2px 4px',
                        fontWeight: 700,
                      }}
                    >
                      Undo
                    </button>
                  )}
                  {isEditable && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteExpense(exp.id);
                      }}
                      title="Delete"
                      aria-label="Delete expense"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--dg)',
                        cursor: 'pointer',
                        padding: 4,
                        opacity: 0.7,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              );
            }}
          />
          {/* Attachments expansion panel */}
          {editingId &&
            attachments[editingId] &&
            attachments[editingId].length > 0 &&
            (() => {
              const idx = expenses.findIndex((e) => e.id === editingId);
              if (idx < 0) return null;
              const isEditable = expenses[idx].status === 'draft';
              return (
                <div
                  style={{
                    background: 'rgba(var(--acr),0.03)',
                    border: '1px solid var(--bd)',
                    borderTop: 'none',
                    borderRadius: '0 0 10px 10px',
                    padding: '8px 16px',
                  }}
                >
                  <div
                    style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}
                  >
                    Attachments
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {attachments[editingId].map((att) => (
                      <div
                        key={att.id}
                        style={{
                          background: 'var(--sf)',
                          border: '1px solid var(--bd)',
                          borderRadius: 6,
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 11,
                        }}
                      >
                        <span style={{ color: 'var(--ac)' }}>{att.fileName}</span>
                        {isEditable && (
                          <button
                            onClick={() => handleDeleteAttachment(att.id, editingId!)}
                            title="Remove attachment"
                            aria-label="Remove attachment"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--dg)',
                              cursor: 'pointer',
                              padding: 4,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                          >
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: 14 }}>
          {[
            { label: 'TOTAL EXPENSES', value: `$${grandTotal.toFixed(2)}`, color: 'var(--ac)' },
            { label: 'LINE ITEMS', value: `${expenses.length}`, color: undefined },
            {
              label: 'DRAFT',
              value: `${draftCount}`,
              color: draftCount > 0 ? 'var(--dg)' : undefined,
            },
            {
              label: 'SUBMITTED',
              value: `${submittedCount}`,
              color: submittedCount > 0 ? 'var(--color-warning)' : undefined,
            },
          ].map((c) => (
            <div
              key={c.label}
              style={{
                background: 'var(--sf)',
                borderRadius: 8,
                border: '1px solid var(--bd)',
                padding: '12px 16px',
                flex: 1,
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--tm)', fontWeight: 600, marginBottom: 4 }}>
                {c.label}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: c.color || 'var(--tx)',
                }}
              >
                {c.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Vacation Tab ───

interface VacationTabProps {
  members: TeamMember[];
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
  memberName: (id?: string) => string;
}

function VacationTab({ members, toast, authUser, memberName }: VacationTabProps) {
  const [categories, setCategories] = useState<VacationCategory[]>([]);
  const [holidays, setHolidays] = useState<FirmHoliday[]>([]);
  const [requests, setRequests] = useState<VacationRequest[]>([]);

  // Request form state
  const [categoryId, setCategoryId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [note, setNote] = useState('');

  // Deny reason
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const myId = authUser?.teamMemberId;
  const canApprove = hasPermission(authUser?.permissions, 'vacation.approve');
  const canViewAll = hasPermission(authUser?.permissions, 'vacation.view_all');

  useEffect(() => {
    loadAll();
  }, []);
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadAll() {
    try {
      const [catsR, holsR, reqsR] = await Promise.allSettled([
        VacationService.categories.list(),
        VacationService.holidays.list(),
        VacationService.listRequests(),
      ]);
      const cats = settled(catsR, [] as VacationCategory[]);
      const hols = settled(holsR, [] as FirmHoliday[]);
      const reqs = settled(reqsR, [] as VacationRequest[]);
      setCategories(cats);
      setHolidays(hols.sort((a, b) => a.date.localeCompare(b.date)));
      setRequests(reqs);
      if (cats.length > 0 && !categoryId) setCategoryId(cats[0].id);
    } catch (err: any) {
      console.error('VacationTab loadAll failed:', err);
    }
  }

  // ─── Computed ───
  function getBusinessDays(start: string, end: string): number {
    let count = 0;
    const d = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    while (d <= e) {
      const dow = d.getDay();
      if (dow > 0 && dow < 6) count++;
      d.setDate(d.getDate() + 1);
    }
    return count;
  }

  const totalHours =
    startDate && endDate ? getBusinessDays(startDate, endDate) * (parseFloat(hoursPerDay) || 8) : 0;

  const myRequests = requests
    .filter((r) => r.teamMemberId === myId)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  const pendingApprovals = canApprove
    ? requests
        .filter((r) => r.status === 'pending' && r.teamMemberId !== myId)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
    : [];

  const allApproved = canViewAll
    ? requests
        .filter((r) => r.status === 'approved')
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
    : [];

  const upcomingHolidays = holidays.filter((h) => h.date >= new Date().toISOString().split('T')[0]);

  function categoryName(id: string): string {
    return categories.find((c) => c.id === id)?.name || '—';
  }

  // ─── Actions ───
  async function submitRequest() {
    if (!categoryId || !startDate || !endDate) {
      toast('Please fill in category, start date, and end date', true);
      return;
    }
    if (startDate > endDate) {
      toast('End date must be after start date', true);
      return;
    }
    if (!myId) {
      toast('No team member linked to your account', true);
      return;
    }

    const bDays = getBusinessDays(startDate, endDate);
    if (bDays === 0) {
      toast('No business days in selected range', true);
      return;
    }

    try {
      await VacationService.saveRequest({
        id: '',
        teamMemberId: myId,
        vacationCategoryId: categoryId,
        startDate,
        endDate,
        hoursPerDay: parseFloat(hoursPerDay) || 8,
        totalHours: bDays * (parseFloat(hoursPerDay) || 8),
        note: note.trim() || undefined,
        status: 'pending',
        createdAt: '',
        updatedAt: '',
      });
      setStartDate('');
      setEndDate('');
      setNote('');
      setHoursPerDay('8');
      await loadAll();
      toast('Vacation request submitted');
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }

  async function cancelRequest(id: string) {
    if (!confirm('Cancel this vacation request?')) return;
    try {
      await VacationService.deleteRequest(id);
      await loadAll();
      toast('Request cancelled');
    } catch (err: any) {
      toast('Cancel failed: ' + err.message, true);
    }
  }

  async function approve(id: string) {
    if (!myId) return;
    try {
      await VacationService.approveRequest(id, myId);
      await loadAll();
      toast('Request approved');
    } catch (err: any) {
      toast('Approve failed: ' + err.message, true);
    }
  }

  async function deny(id: string) {
    if (!myId) return;
    try {
      await VacationService.denyRequest(id, myId, denyReason.trim() || undefined);
      setDenyingId(null);
      setDenyReason('');
      await loadAll();
      toast('Request denied');
    } catch (err: any) {
      toast('Deny failed: ' + err.message, true);
    }
  }

  const statusBadge = (status: string) => {
    const colors: Record<string, { bg: string; fg: string }> = {
      pending: {
        bg: 'color-mix(in srgb, var(--color-warning) 15%, transparent)',
        fg: 'var(--color-warning)',
      },
      approved: { bg: 'rgba(var(--okr),0.15)', fg: 'var(--ok)' },
      denied: {
        bg: 'color-mix(in srgb, var(--color-danger) 15%, transparent)',
        fg: 'var(--dg)',
      },
    };
    const c = colors[status] || colors.pending;
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          background: c.bg,
          color: c.fg,
        }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px',
    borderRadius: 5,
    border: '1px solid var(--bd)',
    background: 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 13,
  };
  const sectionTitle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--tx)',
    margin: '0 0 10px',
  };
  const thStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--tm)',
    textTransform: 'uppercase',
    textAlign: 'left',
    padding: '6px 10px',
    borderBottom: '1px solid var(--bd)',
    background: 'var(--sa)',
  };
  const tdStyle: React.CSSProperties = {
    fontSize: 13,
    padding: '8px 10px',
    borderBottom: '1px solid var(--bd)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ═══ Submit Request ═══ */}
      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 10,
          border: '1px solid var(--bd)',
          padding: 20,
        }}
      >
        <h3 style={sectionTitle}>Submit Time-Off Request</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
              Category
            </div>
            <div style={{ minWidth: 180 }}>
              <SearchableSelect
                value={categoryId}
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(v) => setCategoryId(v)}
                placeholder={categories.length === 0 ? 'No categories' : '-- Pick category --'}
                searchPlaceholder="Search categories…"
                clearable={false}
                style={inputStyle}
              />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
              Start Date
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
              End Date
            </div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
              Hrs/Day
            </div>
            <input
              type="number"
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(e.target.value)}
              step="0.5"
              min="0.5"
              max="24"
              style={{ ...inputStyle, width: 70, fontFamily: "'JetBrains Mono', monospace" }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
              Note (optional)
            </div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason..."
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <Button onClick={submitRequest} variant="default" style={{ whiteSpace: 'nowrap' }}>
            Submit Request
          </Button>
        </div>
        {totalHours > 0 && (
          <div style={{ marginTop: 8, fontSize: 13, color: 'var(--tm)' }}>
            {getBusinessDays(startDate, endDate)} business day
            {getBusinessDays(startDate, endDate) !== 1 ? 's' : ''} &middot; {totalHours} total hours
          </div>
        )}
      </div>

      {/* ═══ My Requests ═══ */}
      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 10,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 16px 0' }}>
          <h3 style={sectionTitle}>My Requests</h3>
        </div>
        <DataTable<VacationRequest>
          columns={[
            {
              key: 'category',
              label: 'Category',
              render: (r) => categoryName(r.vacationCategoryId),
              getValue: (r) => categoryName(r.vacationCategoryId),
            },
            { key: 'startDate', label: 'Start', getValue: (r) => r.startDate },
            { key: 'endDate', label: 'End', getValue: (r) => r.endDate },
            {
              key: 'hoursPerDay',
              label: 'Hrs/Day',
              width: 80,
              render: (r) => (
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.hoursPerDay}</span>
              ),
              getValue: (r) => r.hoursPerDay,
            },
            {
              key: 'totalHours',
              label: 'Total',
              width: 80,
              render: (r) => (
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {r.totalHours || '\u2014'}
                </span>
              ),
              getValue: (r) => r.totalHours || 0,
            },
            {
              key: 'status',
              label: 'Status',
              width: 100,
              render: (r) => statusBadge(r.status),
              getValue: (r) => r.status,
            },
            {
              key: 'note',
              label: 'Note',
              render: (r) => (
                <span style={{ color: 'var(--tm)', fontSize: 11 }}>
                  {r.note || '\u2014'}
                  {r.denialReason ? ` (Denied: ${r.denialReason})` : ''}
                </span>
              ),
              getValue: (r) => r.note || '',
            },
          ]}
          data={myRequests}
          keyField="id"
          storageKey="vacation-my-requests"
          emptyTitle="No Requests"
          emptyMessage="No vacation requests yet."
          actions={(r) =>
            r.status === 'pending' ? (
              <button
                onClick={() => cancelRequest(r.id)}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: 'none',
                  background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
                  color: 'var(--dg)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            ) : undefined
          }
        />
      </div>

      {/* ═══ Approval Queue (permission-gated) ═══ */}
      {canApprove && (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: 10,
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '14px 16px 0' }}>
            <h3 style={sectionTitle}>Pending Approvals</h3>
          </div>
          <DataTable<VacationRequest>
            columns={[
              {
                key: 'member',
                label: 'Member',
                render: (r) => (
                  <span style={{ fontWeight: 600 }}>{memberName(r.teamMemberId)}</span>
                ),
                getValue: (r) => memberName(r.teamMemberId),
              },
              {
                key: 'category',
                label: 'Category',
                render: (r) => categoryName(r.vacationCategoryId),
                getValue: (r) => categoryName(r.vacationCategoryId),
              },
              { key: 'startDate', label: 'Start', getValue: (r) => r.startDate },
              { key: 'endDate', label: 'End', getValue: (r) => r.endDate },
              {
                key: 'hoursPerDay',
                label: 'Hrs/Day',
                width: 80,
                render: (r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.hoursPerDay}</span>
                ),
                getValue: (r) => r.hoursPerDay,
              },
              {
                key: 'totalHours',
                label: 'Total',
                width: 80,
                render: (r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.totalHours || '\u2014'}
                  </span>
                ),
                getValue: (r) => r.totalHours || 0,
              },
              {
                key: 'note',
                label: 'Note',
                render: (r) => (
                  <span style={{ color: 'var(--tm)', fontSize: 11 }}>{r.note || '\u2014'}</span>
                ),
                getValue: (r) => r.note || '',
              },
            ]}
            data={pendingApprovals}
            keyField="id"
            storageKey="vacation-pending-approvals"
            emptyTitle="No Pending Requests"
            emptyMessage="No pending requests to review."
            actions={(r) =>
              denyingId === r.id ? (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    value={denyReason}
                    onChange={(e) => setDenyReason(e.target.value)}
                    placeholder="Reason..."
                    style={{ ...inputStyle, padding: '3px 6px', fontSize: 11, width: 100 }}
                  />
                  <button
                    onClick={() => deny(r.id)}
                    style={{
                      padding: '3px 8px',
                      borderRadius: 4,
                      border: 'none',
                      background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
                      color: 'var(--dg)',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Deny
                  </button>
                  <button
                    onClick={() => {
                      setDenyingId(null);
                      setDenyReason('');
                    }}
                    title="Cancel"
                    aria-label="Cancel"
                    style={{
                      padding: '3px 6px',
                      borderRadius: 4,
                      border: 'none',
                      background: 'var(--sa)',
                      color: 'var(--tm)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={11} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => approve(r.id)}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 4,
                      border: 'none',
                      background: 'rgba(var(--okr),0.15)',
                      color: 'var(--ok)',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setDenyingId(r.id)}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 4,
                      border: 'none',
                      background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
                      color: 'var(--dg)',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Deny
                  </button>
                </div>
              )
            }
          />
        </div>
      )}

      {/* ═══ Approved Time Off (visible to managers) ═══ */}
      {canViewAll && allApproved.length > 0 && (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: 10,
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '14px 16px 0' }}>
            <h3 style={sectionTitle}>Approved Time Off</h3>
          </div>
          <DataTable<VacationRequest>
            columns={[
              {
                key: 'member',
                label: 'Member',
                render: (r) => (
                  <span style={{ fontWeight: 600 }}>{memberName(r.teamMemberId)}</span>
                ),
                getValue: (r) => memberName(r.teamMemberId),
              },
              {
                key: 'category',
                label: 'Category',
                render: (r) => categoryName(r.vacationCategoryId),
                getValue: (r) => categoryName(r.vacationCategoryId),
              },
              { key: 'startDate', label: 'Start', getValue: (r) => r.startDate },
              { key: 'endDate', label: 'End', getValue: (r) => r.endDate },
              {
                key: 'totalHours',
                label: 'Total Hours',
                width: 100,
                render: (r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.totalHours || '\u2014'}
                  </span>
                ),
                getValue: (r) => r.totalHours || 0,
              },
            ]}
            data={allApproved}
            keyField="id"
            storageKey="vacation-approved"
            emptyTitle="No Approved Time Off"
            emptyMessage="No approved time off to display."
          />
        </div>
      )}

      {/* ═══ Upcoming Holidays ═══ */}
      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 10,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 16px 0' }}>
          <h3 style={sectionTitle}>Upcoming Firm Holidays</h3>
        </div>
        {upcomingHolidays.length === 0 ? (
          <EmptyState
            title="No upcoming holidays"
            subtitle="Holidays can be set in Admin > Time Off > Firm Holidays."
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 16px 16px' }}>
            {upcomingHolidays.map((h) => (
              <div
                key={h.id}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  background: 'rgba(var(--acr),0.06)',
                  border: '1px solid rgba(var(--acr),0.15)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>{h.name}</span>
                <span style={{ fontSize: 11, color: 'var(--tm)' }}>
                  {h.date} &middot; {h.hours}h
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Time & Expense Page (combined tabs) ─────────────────────────

type TimeExpenseTab = 'timesheets' | 'forecasting' | 'expenses' | 'vacation';

interface TimeExpensePageProps extends PageProps {
  /** When set by a cross-page drill, switch to the requested tab and
   *  pre-filter inner pages to the supplied projectId / weekStart /
   *  memberId. The page calls onDeepLinkConsumed exactly once after
   *  applying so the same link doesn't re-trigger on re-render. */
  deepLink?: {
    view: 'time-expense';
    tab?: TimeExpenseTab;
    projectId?: string;
    weekStart?: string;
    memberId?: string;
  } | null;
  onDeepLinkConsumed?: () => void;
}

function TimeExpensePage({ toast, authUser, deepLink, onDeepLinkConsumed }: TimeExpensePageProps) {
  const [tab, setTab] = useState<TimeExpenseTab>('timesheets');
  const [members, setMembers] = useState<TeamMember[]>([]);
  // Initial filter for inner pages — wiped after they consume.
  const [initialFilter, setInitialFilter] = useState<{
    projectId?: string;
    weekStart?: string;
    memberId?: string;
  } | null>(null);

  useEffect(() => {
    TeamService.list().then((m) => setMembers(m.filter((x) => x.active)));
  }, []);

  // One-shot deep-link consumer. The dashboard's "Forecast next 4 weeks"
  // bar-click → forecast-week drill lands here as e.g.
  // { tab:'forecasting', projectId:'...', weekStart:'2026-05-12' }.
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.tab) setTab(deepLink.tab);
    if (deepLink.projectId || deepLink.weekStart || deepLink.memberId) {
      setInitialFilter({
        projectId: deepLink.projectId,
        weekStart: deepLink.weekStart,
        memberId: deepLink.memberId,
      });
    }
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  const memberName = useCallback(
    (id?: string) => {
      if (!id) return '';
      return members.find((m) => m.id === id)?.name || '';
    },
    [members]
  );

  const tabs: { id: TimeExpenseTab; label: string }[] = [
    { id: 'timesheets', label: 'Timesheets' },
    { id: 'forecasting', label: 'Forecasting' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'vacation', label: 'Vacation' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 16,
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? 'var(--ac)' : 'var(--tm)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              borderBottom: tab === t.id ? '2px solid var(--ac)' : '2px solid transparent',
              transition: 'all .15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {tab === 'timesheets' && (
          <TimeSheetsPage
            toast={toast}
            authUser={authUser}
            initialFilter={initialFilter}
            onInitialFilterConsumed={() => setInitialFilter(null)}
          />
        )}
        {tab === 'forecasting' && (
          <NewForecastingPage
            currentUserId={authUser?.teamMemberId || ''}
            currentUserPermissions={authUser?.permissions || []}
            initialFilter={initialFilter}
            onInitialFilterConsumed={() => setInitialFilter(null)}
          />
        )}
        {tab === 'expenses' && <ExpensesPage toast={toast} authUser={authUser} />}
        {tab === 'vacation' && (
          <VacationTab
            members={members}
            toast={toast}
            authUser={authUser}
            memberName={memberName}
          />
        )}
      </div>
    </div>
  );
}

// ─── Exports ─────────────────────────────────────────────────────

const TimeTrackingPage = TimeSheetsPage;
// `ForecastingPage` here is the standalone forecasting grid — it's the
// imported one from ./ForecastingPage, re-exported for any consumer that
// imports from TimeTrackingPage.
export {
  TimeSheetsPage,
  NewForecastingPage as ForecastingPage,
  ExpensesPage,
  TimeTrackingPage,
  TimeExpensePage,
};
